"use client";

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

export default function PrivateDining() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    guests: '',
    message: ''
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert('Thank you! We will contact you within 24 hours to discuss your event.');
    setFormData({ name: '', email: '', phone: '', date: '', guests: '', message: '' });
  };

  const handleChange = (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
  {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section className="py-24 md:py-32 px-6 md:px-12 bg-muted/20 border-t border-border/50">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Content Side */}
          <div className="space-y-8 animate-fade-in">
            <span className="text-accent text-xs font-bold tracking-[0.3em] uppercase block">
              Exclusive Events
            </span>

            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.1] text-foreground">
              Private Dining & <span className="italic font-light">Catering</span>
            </h2>

            <p className="text-lg text-muted-foreground leading-relaxed">
              Host your next celebration in our elegant private dining room. Whether it's a
              corporate dinner, wedding reception, or intimate gathering, we create unforgettable
              experiences tailored to your vision.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Icon name="UserGroupIcon" size={24} className="text-primary" />
                </div>
                <div>
                  <h4 className="font-bold text-base text-foreground mb-1">Capacity</h4>
                  <p className="text-sm text-muted-foreground">Up to 50 guests</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Icon name="CakeIcon" size={24} className="text-primary" />
                </div>
                <div>
                  <h4 className="font-bold text-base text-foreground mb-1">Custom Menus</h4>
                  <p className="text-sm text-muted-foreground">Tailored to your event</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Icon name="TruckIcon" size={24} className="text-primary" />
                </div>
                <div>
                  <h4 className="font-bold text-base text-foreground mb-1">Catering</h4>
                  <p className="text-sm text-muted-foreground">Off-site events available</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Icon name="MusicalNoteIcon" size={24} className="text-primary" />
                </div>
                <div>
                  <h4 className="font-bold text-base text-foreground mb-1">Entertainment</h4>
                  <p className="text-sm text-muted-foreground">Live music available</p>
                </div>
              </div>
            </div>

            {/* Image */}
            <div className="image-zoom-container aspect-video rounded-3xl shadow-xl mt-8 lg:hidden">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_14db64811-1768854357977.png"
                alt="Elegant private dining room with long table set for formal event, ambient lighting, and modern decor"
                className="w-full h-full object-cover" />

            </div>
          </div>

          {/* Form Side */}
          <div className="bg-card rounded-3xl p-8 md:p-10 border border-border/50 shadow-2xl animate-slide-up delay-200">
            <h3 className="font-serif text-3xl text-foreground mb-6">Inquiry Form</h3>
            <p className="text-sm text-muted-foreground mb-8">
              Fill out the form below and we'll contact you within 24 hours to discuss your event.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  placeholder="John Doe" />

              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  placeholder="john@example.com" />

              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  placeholder="(555) 123-4567" />

              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="date" className="block text-sm font-medium text-foreground mb-2">
                    Event Date *
                  </label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" />

                </div>

                <div>
                  <label htmlFor="guests" className="block text-sm font-medium text-foreground mb-2">
                    Number of Guests *
                  </label>
                  <select
                    id="guests"
                    name="guests"
                    value={formData.guests}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all">

                    <option value="">Select</option>
                    <option value="10-20">10-20</option>
                    <option value="20-30">20-30</option>
                    <option value="30-40">30-40</option>
                    <option value="40-50">40-50</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                  Event Details
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                  placeholder="Tell us about your event (occasion, preferences, special requests...)">
                </textarea>
              </div>

              <button
                type="submit"
                className="w-full px-8 py-4 bg-primary text-primary-foreground rounded-full text-base font-bold uppercase tracking-wider hover:scale-105 active:scale-95 transition-transform shadow-xl flex items-center justify-center gap-3">

                <Icon name="PaperAirplaneIcon" size={20} />
                <span>Submit Inquiry</span>
              </button>
            </form>
          </div>
        </div>

        {/* Desktop Image */}
        <div className="hidden lg:block image-zoom-container aspect-[21/9] rounded-[40px] shadow-2xl mt-16">
          <AppImage
            src="https://img.rocket.new/generatedImages/rocket_gen_img_14db64811-1768854357977.png"
            alt="Elegant private dining room with long table set for formal event, ambient lighting, and modern decor"
            className="w-full h-full object-cover" />

        </div>
      </div>
    </section>);

}